import ReactMarkdown from "react-markdown"

import remarkGfm from "remark-gfm"

import { FileBadge } from "@components/badges/FileBadge"

interface MarkdownDisplayProps {
    content: string
}
export function MarkdownDisplay({ content }: Readonly<MarkdownDisplayProps>) {
    const convertedContent = convertLinks(processContentForMarkdown(content)) as string

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({ ...props }: { href?: string; children?: React.ReactNode }) => {
                    const href = props.href || ""

                    if (href.startsWith("/store/objects/")) {
                        const documentId = href.split("/store/objects/")[1]
                        return <FileBadge id={documentId} name={String(props?.children || "")} />
                    }
                    return <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800" />
                },
                p: ({ ...props }: { children?: React.ReactNode }) => {
                    return <span {...props} className="my-2 block text-gray-800 whitespace-pre-wrap leading-relaxed" />
                },
                strong: ({ ...props }: { children?: React.ReactNode }) => <strong {...props} className="font-bold text-gray-900" />,
                em: ({ ...props }: { children?: React.ReactNode }) => <em {...props} className="italic text-gray-800" />,
                pre: ({ ...props }: { children?: React.ReactNode }) => <pre {...props} className="my-2 bg-gray-800 p-3 rounded-md text-gray-100 overflow-x-auto border border-gray-700" />,
                code: ({ className, children, ...props }: { className?: string; children?: React.ReactNode }) => {
                    const match = /language-(\w+)/.exec(className || "")
                    const isInline = !match
                    const language = match ? match[1] : ""

                    return (
                        <>
                            {!isInline && language && <div className="code-language-indicator">{language}</div>}
                            <code {...props}>{children}</code>
                        </>
                    )
                },
                h1: ({ ...props }: { children?: React.ReactNode }) => <h1 {...props} className="text-gray-900 font-bold text-2xl my-3" />,
                h2: ({ ...props }: { children?: React.ReactNode }) => <h2 {...props} className="text-gray-900 font-bold text-xl my-2" />,
                h3: ({ ...props }: { children?: React.ReactNode }) => <h3 {...props} className="text-gray-900 font-bold text-lg my-2" />,
                li: ({ ...props }: { children?: React.ReactNode }) => {
                    return <li {...props} className="text-gray-800 my-2 pl-1" />
                },
                ul: ({ ...props }: { children?: React.ReactNode }) => <ul {...props} className="my-3 pl-6 list-disc space-y-1" />,
                ol: ({ ...props }: { children?: React.ReactNode }) => <ol {...props} className="my-3 pl-6 list-decimal space-y-1" />,
                blockquote: ({ ...props }: { children?: React.ReactNode }) => <blockquote {...props} className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-3" />,
                hr: ({ ...props }: { children?: React.ReactNode }) => <hr {...props} className="border-gray-300 my-4" />,
                table: ({ ...props }: { children?: React.ReactNode }) => (
                    <div className="overflow-x-auto my-4">
                        <table {...props} className="min-w-full border-collapse border border-gray-300" />
                    </div>
                ),
                th: ({ ...props }: { children?: React.ReactNode }) => <th {...props} className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-gray-700" />,
                td: ({ ...props }: { children?: React.ReactNode }) => <td {...props} className="border border-gray-300 px-4 py-2 text-gray-800" />,
            }}
        >
            {convertedContent}
        </ReactMarkdown>
    )
}

function processContentForMarkdown(content: string | object) {
    if (typeof content !== "string") {
        return content
    }

    if (/\d+\.\s+.+/.test(content) && !content.includes("\n\n")) {
        return content.replace(/(\d+\.\s+.+?)(?=\s+\d+\.\s+|$)/g, "$1\n\n")
    }

    return content
}

function convertLinks(content: string | object) {
    if (typeof content !== "string") {
        return content
    }

    return content.replace(/\[([^\]]+)\]\(store:([a-f\d]{24})\)/gi, (_, text, id) => {
        return `[${text}](/store/objects/${id})`
    })
}
